import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, LogOut } from "lucide-react";
import SEO from "../components/SEO";
import { supabase } from "../supabaseClient";
import Calculators from "./Calculators";

export default function Dashboard() {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const checkUser = async () => {
            try {
                const { data: { session } } = await supabase.auth.getSession();
                if (session) {
                    setUser(session.user);
                } else {
                    navigate("/login");
                }
            } catch (err) {
                console.error("Error checking auth session:", err);
                navigate("/login");
            } finally {
                setLoading(false);
            }
        };

        checkUser();

        // Listen for auth state changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            if (session) {
                setUser(session.user);
            } else {
                setUser(null);
                navigate("/login");
            }
        });

        return () => {
            subscription.unsubscribe();
        };
    }, [navigate]);

    const handleLogout = async () => {
        try {
            await supabase.auth.signOut();
            navigate("/login");
        } catch (err) {
            console.error("Error signing out:", err);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="flex flex-col items-center gap-4">
                    <svg className="animate-spin h-10 w-10 text-accent" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <p className="text-secondary text-sm font-medium">Loading your dashboard...</p>
                </div>
            </div>
        );
    }

    if (!user) return null;

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300 py-12 px-4 sm:px-6 lg:px-8">
            <SEO title="User Dashboard" description="Manage your business financials, invoices, and accounting analytics." />
            
            <div className="max-w-7xl mx-auto space-y-8 animate-fade-in">
                {/* Header Widget */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 p-8 rounded-3xl bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border border-slate-200/50 dark:border-slate-800/50 shadow-xl">
                    <div className="space-y-2">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-bold uppercase tracking-wider">
                            <Activity className="w-3.5 h-3.5" /> Workspace Live
                        </div>
                        <h1 className="text-3xl font-black text-primary dark:text-white leading-tight">
                            Welcome Back, <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-emerald-500">
                                {user.user_metadata?.full_name || user.email.split("@")[0]}
                            </span>
                        </h1>
                        <p className="text-secondary text-sm dark:text-gray-400">
                            Logged in as: <span className="font-semibold text-primary dark:text-white">{user.email}</span>
                        </p>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-red-500/20 bg-red-500/5 hover:bg-red-500/10 text-red-500 text-sm font-bold transition-all transform active:scale-95 cursor-pointer"
                    >
                        <LogOut className="w-4 h-4" /> Sign Out
                    </button>
                </div>

                {/* Embedded Calculators Workspace */}
                <div className="bg-white dark:bg-slate-900 p-8 rounded-[2rem] border border-slate-200/50 dark:border-slate-800/50 shadow-lg">
                    <Calculators />
                </div>
            </div>
        </div>
    );
}
